import { useState, useEffect, useCallback, useMemo, type FC } from 'react';
import { useFragment, graphql } from 'react-relay';
import classnames from 'classnames';
import { useDeviceType } from '../../../nftHeader/hooks/useDeviceType.tsx';
import { NftMarketFiltersResponsive } from './NftMarketFiltersResponsive.tsx';
import { NftMarketFiltersMobile } from './NftMarketFiltersMobile.tsx';
import { NftMarketActiveFilterTags } from './NftMarketActiveFilterTags.tsx';

import { getFilterConfig, initialFilters } from './filterConfig.ts';
import { type FilterShape, type FilterConfig } from './filterConfig.ts';

import { filterStateToUrl } from './filterTransformations.ts';
import { makeSearchFacetCountGetter } from '../../../utils/getSearchFacetCount.ts';

import { type NftMarketSortAndFilter_search$key } from './__generated__/NftMarketSortAndFilter_search.graphql.ts';

import styles from './NftMarketSortAndFilter.scss';

const getActiveFilterCount = (filterConfig: FilterConfig, state: FilterShape): number => {
    let result = 0;
    for (const filterKey in state) {
        if (state.hasOwnProperty(filterKey)) {
            const config = filterConfig[filterKey as keyof FilterShape];
            const value = state[filterKey as keyof FilterShape];
            if (value) {
                const valueArr = config.isArray ? value : [value];
                result += valueArr.length;
            }
        }
    }

    return result;
};

type Props = {
    onFilterChange: (filters: FilterShape, filterUrl: URL) => void;
    search?: NftMarketSortAndFilter_search$key | null | undefined;
    totalResults: number;
    isHidden: boolean;
    startingFilters: FilterShape;
};

export const NftMarketSortAndFilter: FC<Props> = ({
    onFilterChange,
    search: searchRef = null,
    totalResults,
    isHidden,
    startingFilters,
}) => {
    const { isMobile } = useDeviceType();
    const [state, setState] = useState<FilterShape>(startingFilters);

    const searchFacets = useFragment(
        graphql`
            fragment NftMarketSortAndFilter_search on NftItemSearchConnection {
                facets {
                    facetField
                    facetValues {
                        value
                        count
                    }
                }
            }
        `,
        searchRef
    );

    const filterConfig = useMemo(() => {
        const getSearchFacetCount = makeSearchFacetCountGetter(searchFacets?.facets);
        return getFilterConfig({
            upcoming: !!getSearchFacetCount('ITEM_SEARCH_AUCTION_UPCOMING'),
            secondaryMarket: !!getSearchFacetCount('ITEM_SEARCH_MARKET_SECONDARY'),
        });
    }, [searchFacets]);

    useEffect(() => {
        setState(startingFilters);
    }, [startingFilters]);

    const handleFilterChange = useCallback(
        (newState: FilterShape) => {
            setState(newState);
            const updatedUrl = filterStateToUrl(
                filterConfig,
                newState,
                new URL(window.location.href)
            );

            // refetch with new filters
            onFilterChange(newState, updatedUrl);
        },
        [onFilterChange, filterConfig]
    );

    const activeFilterCount = getActiveFilterCount(filterConfig, state);

    return (
        <div className={classnames(styles.container, { [styles.hidden]: isHidden })}>
            {isMobile ? (
                <NftMarketFiltersMobile
                    state={state}
                    filterConfig={filterConfig}
                    onFiltersChange={handleFilterChange}
                    totalResults={totalResults}
                    activeFilterCount={activeFilterCount}
                />
            ) : (
                <NftMarketFiltersResponsive
                    state={state}
                    filterConfig={filterConfig}
                    onFiltersChange={handleFilterChange}
                />
            )}

            <NftMarketActiveFilterTags
                filterConfig={filterConfig}
                state={state}
                showClearAll={isMobile && activeFilterCount > 0}
                onClearAll={() => handleFilterChange(initialFilters)}
                onDeleteFilter={deletedValue => {
                    const newState = { ...state };

                    for (const [filter, value] of Object.entries(state)) {
                        if (state.hasOwnProperty(filter)) {
                            if (Array.isArray(value)) {
                                // @ts-ignore "Type 'NftItemSearchMediumType[]' is not assignable to type '"%future added value" & NftItemSearchMediumType[]"
                                newState[filter as keyof FilterShape] = value.filter(
                                    member => member !== deletedValue
                                );
                            } else if (value === deletedValue) {
                                newState[filter as keyof FilterShape] = null;
                            }
                        }
                    }
                    handleFilterChange(newState);
                }}
            />
        </div>
    );
};
